import React, { useRef, useState } from "react";
import { 
	RCCBadge, 
	RCCButton, 
	RCCGrid, 
	RCCTooltip,  
	RCCInput,
	RCCHookToast,
	RCCServicesApi
} from "react-common-components";
import { IForwardPagination } from "react-common-components/dist/components/Pagination/interface";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import Pagination from "src/components/Pagination";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import Select from "src/components/Select";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import formatDate from "src/utils/formatDate";
import formatNumber from "src/utils/formatNumber";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { standardErrorMessages } from "src/utils/errors";
import ModalImportStructure from "./modalImportValues";
import { IRefModalImportStructure } from "./interface";

const FinancialManagementAccounting: React.FC = () => { 
	const { translate, currentLanguage } = useTranslation();
	const { confirm } = useConfirmModal();
	const { addToast } = RCCHookToast();
	const refModalImportStructure = useRef<IRefModalImportStructure>(null);

	const [showCheckboxes, setShowCheckboxes] = useState(false);

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ 
		centerCost: string,
		account: string,
		item1: string,
		item2: string,
		integrated: string,
		unit: string,
		startDate?: Date | null,
    	endDate?: Date | null
	}>({ 
		centerCost: "", 
		account: "", 
		item1: "", 
		item2: "", 
		integrated: "T", 
		unit: "",
		startDate: null,
		endDate: null
	});
	const { 
		handleSubmit: handleSubmitFilters, 
		control: controlFilters, 
		register: registerFilters, 
		reset: resetFilters,
		setValue: setValueFilters
	} = useForm();
	const submitFilters = (data: any) => {
		setFilters({ 
			...data,
			startDate: data.startDate ? `01/${formatDate(data.startDate, "pt", false, "dd/")}` : "",
			endDate: data.endDate ? `01/${formatDate(data.endDate, "pt", false, "dd/")}` : ""
		});
		paginationRef.current?.reload();
	};

	// List & Pagination
	const paginationRef = useRef<IForwardPagination>(null);

	const colIntegrated = (row: any) => (
		<RCCBadge color={row.ofv001Integrado === "S" ? "green" : "red"}
			styleInline="font-size: 0.83rem;"
		>
			{row.ofv001Integrado === "S" ? translate("Sim") : translate("Não")}
		</RCCBadge>
	);

	const deleteItems = () => {
		const selectedItems = paginationRef.current?.getSelectedRows();

		if(!selectedItems || selectedItems.length === 0){
			addToast({
				color: "yellow",
				description: translate("É necessário selecionar ao menos um item"),
				title: translate("Atenção")
			});
			return;
		}
		
		confirm(
			translate("Excluir valores contábeis"),
			`${translate("Tem certeza que deseja excluir os registros selecionados")} ?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "FinancialManagementAccounting/Delete",
					urlParameters: {
						items: selectedItems.map((item) => item.row.ofv001Id)
					}
				});

				paginationRef.current?.reload();

				addToast({
					color: "green",
					description: translate("Valor contábil excluído com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir valor contábil"), addToast);
			},
			true
		);
	};

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
				{/* Header -> Results + Buttons */}
				<PageTitle
					title={translate("Valores contábeis")}
					icon={() => <AppIcons name="financialManagement" />}
				>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
						<RCCTooltip
							text={translate("Selecionar valores contábeis")}
							color={showCheckboxes ? "blue" : "grey"}
						>
							<RCCButton
								icon={{name: "checkSquare", color: "#FFF", size: 16}}
								color={showCheckboxes ? "blue" : "grey"}
								layout="isRounded"
								size="xs"
								onClick={() => {
									paginationRef.current?.clearSelectedRows();
									setShowCheckboxes((prevState) => !prevState);
								}}
							/>
						</RCCTooltip>
					</CheckPermission>
					<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
						<RCCTooltip
							text={translate("Importar valores contábeis")}
							color="purple"
						>
							<RCCButton
								icon={{name: "upload", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModalImportStructure.current?.openModalImportStructure(); }}
								size="md"
							/>
						</RCCTooltip>
					</CheckPermission>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || 
							filters.account ||
							filters.centerCost || 
							filters.item1 || 
							filters.item2 || 
							filters.unit || 
							filters.startDate ||
							filters.endDate ||
							filters.integrated !== "T" ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || 
								filters.account ||
								filters.centerCost || 
								filters.item1 || 
								filters.item2 ||
								filters.unit || 
								filters.startDate ||
								filters.endDate ||
								filters.integrated !== "T" ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
				</PageTitle>

				{/* Filters */}
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						centerCost: "", account: "", item1: "", item2: "", integrated: "T", unit: "", startDate: null, endDate: null
					})}
					showFilters={showFilters}
				>
					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<DateTimePickerRange
								control={controlFilters}
								label={translate("Período")}
								labelSeparator={translate("até")}
								nameInit = "startDate"
								nameEnd = "endDate"
								defaultValueEnd={filters.endDate}
								defaultValueInit={filters.startDate}
								type="datePicker"
								setValue={setValueFilters}
								props={{
									datePicker: {
										format: "MM/yyyy",
										views: ["year", "month"]
									}
								}}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="centerCost" 
								label={translate("Centro de custo")}
								register={registerFilters}
								defaultValue={filters.centerCost}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="account" 
								label={translate("Conta contábil")}
								register={registerFilters}
								defaultValue={filters.account}
							/>
						</RCCGrid>
					</RCCGrid>

					<RCCGrid
						container
						item
						spacing={1}
					>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="unit" 
								label={translate("Unidade/Filial")}
								register={registerFilters}
								defaultValue={filters.unit}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="item1" 
								label={`${translate("Item contábil")} 1`}
								register={registerFilters}
								defaultValue={filters.item1}
							/>
						</RCCGrid>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<RCCInput
								name="item2" 
								label={`${translate("Item contábil")} 2`}
								register={registerFilters}
								defaultValue={filters.item2}
							/>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid container item spacing={1}>
						<RCCGrid
							container 
							item
							xs={12}
							sm={6}
							md={4}
							lg={4}
						>
							<Select 
								control={controlFilters}
								name="integrated" 
								options={[
									{value: "S", label: translate("Sim")}, 
									{value: "N", label: translate("Não")},
									{value: "T", label: translate("Todos")}
								]} 
								label={translate("Integrado")}
								defaultValue={filters.integrated}
							/>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>

				{/* List */}
				<Pagination 
					ref={paginationRef}
					selectRows={showCheckboxes ? "multiple" : undefined}
					dataOrRoute="FinancialManagementAccounting/List"
					filtersData={filters}    
					columns={[{
						idColumn: "ofv001Data",
						nameColumn: translate("Data"),
						xs: 6,
						sm: 1,
						cellRenderer: (row: any) => <>{formatDate(row.ofv001Data, "pt", false, "dd/")}</>
	  				},
					{
					  idColumn: "ofv001Unidade",
					  nameColumn: translate("Unidade/Filial"),
					  xs: 6,
					  sm: 1
					},
					{
						idColumn: "ofv001Ccusto",
						nameColumn: translate("Centro de custo"),
						xs: 12,
						sm: 2,
						cellRenderer: (row: any) => <>{row.ofv001Descrccusto} {row.ofv001Ccusto !== null && row.ofv001Ccusto.length > 0 ? `(${row.ofv001Ccusto})` : ""}</>
					},
					{
						idColumn: "ofv001Conta",
						nameColumn: translate("Conta"),
						xs: 12,
						sm: 2,
						cellRenderer: (row: any) => <>{row.ofv001Descrconta} {row.ofv001Conta !== null && row.ofv001Conta.length > 0 ? `(${row.ofv001Conta})` : ""}</>
					},
					{
						idColumn: "ofv001Item1",
						nameColumn: `${translate("Item contábil")} 1`,
						xs: 12,
						sm: 1,
						cellRenderer: (row: any) => <>{row.ofv001Descritem1} {row.ofv001Item1 !== null && row.ofv001Item1.length > 0 ? `(${row.ofv001Item1})` : ""}</>
					},
					{
						idColumn: "ofv001Item2",
						nameColumn: `${translate("Item contábil")} 2`,
						xs: 12,
						sm: 1,
						cellRenderer: (row: any) => <>{row.ofv001Descritem2} {row.ofv001Item2 !== null && row.ofv001Item2.length > 0 ? `(${row.ofv001Item2})` : ""}</>
					},
					{
						idColumn: "ofv001Credito",
						nameColumn: translate("Crédito"),
						xs: 12,
						sm: 1,
						cellRenderer: (row: any) => <>{formatNumber(row.ofv001Credito, currentLanguage)}</>
					},
					{
						idColumn: "ofv001Debito",
						nameColumn: translate("Débito"),
						xs: 12,
						sm: 1,
						cellRenderer: (row: any) => <>{formatNumber(row.ofv001Debito, currentLanguage)}</>
					},
					{
						idColumn: "ofv001Saldo",
						nameColumn: translate("Saldo"),
						xs: 12,
						sm: 1,
						cellRenderer: (row: any) => <>{formatNumber(row.ofv001Saldo, currentLanguage)}</>
					},
					{
						idColumn: "ofv001Integrado",
						nameColumn: translate("Integrado"),
						xs: 6,
						sm: 1,
						cellRenderer: colIntegrated
	  				}]}
					styleInline={{
						row: "min-height: 34px;"
					}}
				/>
				{showCheckboxes && (
					<RCCGrid 
						container 
						item 
						justifyContent="flex-end"
						styleInline={`
							position: fixed;
							display: flex;
							align-items: center;
							bottom: 1.5rem;
							right: 1.5rem;
							z-index: 2000;
						`}
					>
						<RCCTooltip
							text={translate("Limpar itens selecionados")}
							color="grey"
						>
							<RCCButton
								icon={{name: "broom", color: "#FFF", size: 16}}
								color="grey"
								layout="isRounded"
								size="xs"
								onClick={() => paginationRef.current?.clearSelectedRows()}
							/>
						</RCCTooltip>
						<RCCTooltip
							text={translate("Excluir itens selecionados")}
							color="red"
						>
							<RCCButton
								icon={{name: "trash", color: "#FFF", size: 16}}
								color="red"
								layout="isRounded"
								size="xs"
								onClick={deleteItems}
							/>
						</RCCTooltip>
					</RCCGrid>
				)}

			</RCCGrid>
			
			<ModalImportStructure 
				ref={refModalImportStructure}
				onClose={() => {
					paginationRef.current?.reload();
				}}
			/>
		</>
	);
};

export default FinancialManagementAccounting;
